/**
 * Slider Configuration
 * Configuration values and utilities for price range sliders
 * 
 * @version 1.0.0
 */

/**
 * Default values for price range sliders
 */
export const SLIDER_DEFAULTS = {
  // Default minimum price when no minimum is available
  minPrice: 0,
  
  // Default maximum price when no maximum is available or max is 0
  maxPrice: 1000,
  
  // Minimum increment when rangeMax <= rangeMin
  minIncrement: 100,
  
  // Minimum step value for slider
  minStep: 1,
  
  // Step calculation divisor (used to calculate: Math.floor((rangeMax - rangeMin) / STEP_DIVISOR))
  stepDivisor: 100,
};

/**
 * NoUI Slider default configuration
 */
export const NOUISLIDER_CONFIG = {
  // Whether to connect the range (show filled area between handles)
  connect: true,
  
  // Whether to show tooltips on handles
  tooltips: false,
  
  // Number formatting configuration
  format: {
    to: function(value) {
      return Math.round(value);
    },
    from: function(value) {
      return Number(value);
    }
  },
};

/**
 * Price display configuration
 */
export const PRICE_DISPLAY_CONFIG = {
  // Selectors for price display elements
  selectors: {
    priceCurrentMin: '[data-price-current-min]',
    priceCurrentMax: '[data-price-current-max]',
    priceRangeMin: '[data-price-range-min]',
    priceRangeMax: '[data-price-range-max]',
    selectedMinPrice: '[data-selected-min-price]',
    selectedMaxPrice: '[data-selected-max-price]',
  },
  
  // Whether to remove decimal parts from formatted money
  removeDecimals: true,
};

/**
 * Utility functions for slider configuration
 */
export const SliderUtils = {
  /**
   * Calculate the appropriate step value for a price range
   * @param {number} rangeMin - Minimum price value
   * @param {number} rangeMax - Maximum price value
   * @param {number} divisor - Step calculation divisor (optional)
   * @returns {number} - Calculated step value
   */
  calculateStep(rangeMin, rangeMax, divisor = SLIDER_DEFAULTS.stepDivisor) {
    const rangeDiff = rangeMax - rangeMin;
    const calculatedStep = Math.floor(rangeDiff / divisor);
    return Math.max(SLIDER_DEFAULTS.minStep, calculatedStep);
  },

  /**
   * Validate and normalize price range values
   * @param {number} rangeMin - Raw minimum price value
   * @param {number} rangeMax - Raw maximum price value
   * @returns {Object} - Normalized range with min and max properties
   */
  normalizeRange(rangeMin, rangeMax) {
    // Ensure we have valid range values
    let normalizedMin = rangeMin;
    let normalizedMax = rangeMax;
    
    if (normalizedMin === undefined || normalizedMin === null || normalizedMin === Number.MAX_VALUE) {
      normalizedMin = SLIDER_DEFAULTS.minPrice;
    }
    
    if (normalizedMax === undefined || normalizedMax === null || normalizedMax === 0) {
      normalizedMax = SLIDER_DEFAULTS.maxPrice;
    }
    
    // Ensure rangeMax is greater than rangeMin
    if (normalizedMax <= normalizedMin) {
      normalizedMax = normalizedMin + SLIDER_DEFAULTS.minIncrement;
    }
    
    return {
      min: normalizedMin,
      max: normalizedMax
    };
  },

  /**
   * Create NoUI Slider configuration object
   * @param {number} currentMin - Current minimum value
   * @param {number} currentMax - Current maximum value
   * @param {number} rangeMin - Range minimum value
   * @param {number} rangeMax - Range maximum value
   * @param {Object} overrides - Optional configuration overrides
   * @returns {Object} - NoUI Slider configuration
   */
  createSliderConfig(currentMin, currentMax, rangeMin, rangeMax, overrides = {}) {
    const step = this.calculateStep(rangeMin, rangeMax);
    
    return {
      start: [currentMin || rangeMin, currentMax || rangeMax],
      step,
      range: {
        'min': [rangeMin],
        'max': [rangeMax]
      },
      ...NOUISLIDER_CONFIG,
      ...overrides
    };
  },

  /**
   * Format money value and optionally remove decimals
   * @param {number} value - Price value to format
   * @param {Function} printMoneyFn - Money formatting function (e.g., from Shopify tools)
   * @param {boolean} removeDecimals - Whether to remove decimal parts
   * @returns {string} - Formatted price string
   */
  formatPrice(value, printMoneyFn, removeDecimals = PRICE_DISPLAY_CONFIG.removeDecimals) {
    if (typeof value === 'string' && (value === 'min' || value === 'max')) {
      return value;
    }
    
    let formattedPrice = printMoneyFn(value);
    
    if (removeDecimals) {
      formattedPrice = formattedPrice.split('.')[0];
    }
    
    return formattedPrice;
  }
};

/**
 * Environment-specific configuration
 */
export const ENVIRONMENT_CONFIG = {
  // Enable debug logging in development
  enableDebugLogging: process.env.NODE_ENV === 'development',
  
  // Performance monitoring in development
  enablePerformanceMonitoring: process.env.NODE_ENV === 'development',
};

/**
 * Complete slider configuration export
 */
export default {
  SLIDER_DEFAULTS,
  NOUISLIDER_CONFIG,
  PRICE_DISPLAY_CONFIG,
  SliderUtils,
  ENVIRONMENT_CONFIG,
};